import { memo, useMemo } from 'react';

import { IKnmPreview } from 'APIPackage/KnmSearchModule/interfaces/IKnmPreview';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';

import styles from './KnmPreview.module.scss';
import { KnmSubtitle } from './KnmSubtitle';
import { parseErknmValue } from '../common/parseErknmValue';
import { MspCodesEnum } from '../../../APIPackage/KnmModule/enums/MspCodesEnum';
import { mspCategoryTitlesByMspCodes } from '../../../KnmPackage/MainModule/constants/mspCategoryTitlesByMspCodes';
import { KnmSubjectTypesEnum } from '../../../KnmErknmPackage/ErknmSubjectModule/components/ErknmSubjectInfo';
import { KnmChangeWarning } from './KnmChangeWarning';

interface IPmFaceProps {
  passportData: IKnmPreview;
}

export const PmFace = memo(({ passportData }: IPmFaceProps) => {
  const isMspCode =
    passportData.pmErknm.subject &&
    (passportData.pmErknm.subject.isIndividualEntrepreneur ||
      passportData.pmErknm.subject.isLegalEntity ||
      passportData.pmErknm.subject.isSmallEnterprise);

  const isSonko = passportData.pmErknm.subject && passportData.pmErknm.subject.isSonko;

  const isShowMspCode =
    passportData?.pmErknm.subject.mspCode && passportData.pmErknm.subject.mspCode !== MspCodesEnum.NON_EXIST;

  const subjectType = passportData?.inspectionErknm?.organizations?.[0].subjectType;
  const country = passportData?.pmErknm?.subject?.country;
  const snils = passportData?.pmErknm?.subject?.snils;
  const document = passportData?.pmErknm?.subject?.document;
  const locationAddress = passportData?.pmErknm?.subject?.locationAddress;
  const workAddress = passportData?.pmErknm?.subject?.workAddress;
  const rafpNza = passportData?.pmErknm?.subject?.rafpNza;
  const organizationNameColText = useMemo(() => {
    switch (subjectType) {
      case KnmSubjectTypesEnum.FIZ:
      case KnmSubjectTypesEnum.IFIZ:
        return 'Фамилия, имя, отчество (при наличии)';
      case KnmSubjectTypesEnum.IUR:
        return 'Наименование';
      case KnmSubjectTypesEnum.FIUR:
        return 'Наименование организации';
      default:
        return 'Наименование проверяемого лица';
    }
  }, [subjectType]);

  return (
    <div className={styles.ColumnContent}>
      <KnmChangeWarning
        date={passportData?.dateReasonChangeControllingSubject}
        oldNameControllingSubject={passportData?.oldNameControllingSubject}
        type={'SUBJECT_OBJECT'}
      />
      <span className={styles.SectionTitle}>Сведения о контролируемом лице</span>
      {subjectType && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Тип контролируемого лица</div>
          <div className={styles.ColValue}>{subjectType}</div>
        </div>
      )}
      {passportData.pmErknm.subject && passportData.pmErknm.subject.inn && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Идентификационный номер налогоплательщика (ИНН)</div>
          <div className={styles.ColValue}>{passportData.pmErknm.subject.inn}</div>
        </div>
      )}
      {passportData.pmErknm.subject && passportData.pmErknm.subject.ogrn && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Основной государственный регистрационный номер (ОГРН)</div>
          <div className={styles.ColValue}>{passportData.pmErknm.subject.ogrn}</div>
        </div>
      )}
      {snils && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Страховой номер индивидуального лицевого счета (СНИЛС)</div>
          <div className={styles.ColValue}>{snils}</div>
        </div>
      )}
      {rafpNza && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Номер записи об аккредитации (НЗА)</div>
          <div className={styles.ColValue}>{rafpNza}</div>
        </div>
      )}
      {passportData.pmErknm.subject && passportData.pmErknm.subject.organizationName && (
        <div className={styles.Row}>
          <div className={styles.ColText}>{organizationNameColText}</div>
          <div className={styles.ColValue}>{passportData.pmErknm.subject.organizationName}</div>
        </div>
      )}
      {country && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Государство, в котором указанное юридическое лицо зарегистрировано</div>
          <div className={styles.ColValue}>{country}</div>
        </div>
      )}
      {isMspCode && (
        <div className={styles.Row}>
          <div className={styles.ColText}>
            Сведения об отнесении контролируемого лица к субъектам малого и среднего бизнеса
          </div>
          <div className={styles.ColValue}>{isMspCode}</div>
        </div>
      )}
      <div className={styles.Row}>
        <div className={styles.ColText}>Наличие проверяемого лица в реестре МСП</div>
        <div className={styles.ColValue}>{isShowMspCode ? 'Да' : 'Нет'}</div>
      </div>
      {isShowMspCode && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Категория субъекта МСП</div>
          <div className={styles.ColValue}>
            {mspCategoryTitlesByMspCodes[passportData?.pmErknm.subject.mspCode as MspCodesEnum]}
          </div>
        </div>
      )}
      {document && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Сведения о документе, удостоверяющем личность</div>
          <div className={styles.ColValue}>{document}</div>
        </div>
      )}
      {locationAddress && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Адрес места нахождения</div>
          <div className={styles.ColValue}>{locationAddress}</div>
        </div>
      )}
      {workAddress && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Место нахождения (осуществления деятельности) контролируемого лица</div>
          <div className={styles.ColValue}>{workAddress}</div>
        </div>
      )}
      {typeof isSonko === 'boolean' && (
        <div className={styles.Row}>
          <div className={styles.ColText}>Наличие проверяемого лица в реестре СОНКО</div>
          <div className={styles.ColValue}>{isSonko ? 'Да' : 'Нет'}</div>
        </div>
      )}
      {passportData.pmErknm.subject && passportData.pmErknm.subject.okveds && (
        <>
          <span className={styles.SectionTitle}>ОКВЭД</span>
          {passportData.pmErknm.subject.okveds.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>ОКВЭД №{index + 1}</span>
              {item.name && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Наименование</div>
                  <div className={styles.ColValue}>{item.name}</div>
                </div>
              )}
            </div>
          ))}
        </>
      )}
      {passportData.pmErknm.objects && passportData.pmErknm.objects.length > 0 && (
        <KnmSubtitle title="Сведения об объектах контроля">
          {passportData.pmErknm.objects.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Объект №{index + 1}</span>
              {item.address && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Местонахождение</div>
                  <div className={styles.ColValue}>{item.address}</div>
                </div>
              )}
              {item.objectType?.value && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Тип объекта</div>
                  <div className={styles.ColValue}>{parseErknmValue(item.objectType.value)?.title}</div>
                </div>
              )}
              {item.objectKind?.value && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Вид объекта</div>
                  <div className={styles.ColValue}>{parseErknmValue(item.objectKind.value)?.title}</div>
                </div>
              )}
              {item.objectSubKind?.value && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Подвид объекта</div>
                  <div className={styles.ColValue}>{parseErknmValue(item.objectSubKind.value)?.title}</div>
                </div>
              )}
              {item.riskCategory?.value && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Категория риска</div>
                  <div className={styles.ColValue}>{parseErknmValue(item.riskCategory.value)?.title}</div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      {passportData.pmErknm.inspectors && passportData.pmErknm.inspectors.length > 0 && (
        <KnmSubtitle title="Сведения о должностных лицах контрольных (надзорных) органов, участвующих в соответствующих профилактических мероприятиях">
          {passportData.pmErknm.inspectors.map((item, index) => (
            <div key={index}>
              <span className={styles.SectionTitle}>Должностное лицо №{index + 1}</span>
              {item.fullName && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>ФИО</div>
                  <div className={styles.ColValue}>{item.fullName}</div>
                </div>
              )}
              {item.position?.value && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Должность</div>
                  <div className={styles.ColValue}>{parseErknmValue(item.position.value)?.title}</div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
      <KnmSubtitle title="Проведение мероприятия в форме совместного (межведомственного) контрольного (надзорного) мероприятия">
        <div className={styles.Row}>
          <div className={styles.ColText}>Проводится совместно</div>
          <div className={styles.ColValue}>{passportData.pmErknm.isHasCollaboratingOrganization ? 'Да' : 'Нет'}</div>
        </div>
        {passportData.pmErknm.collaboratingOrganizations && passportData.pmErknm.collaboratingOrganizations.length > 0 && (
          <>
            <span className={styles.SectionTitle}>
              Наименование органов контроля , с которым КНМ проводится совместно
            </span>
            {passportData.pmErknm.collaboratingOrganizations.map((item, index) => (
              <div key={index}>
                <span className={styles.SectionTitle}>Орган контроля №{index + 1}</span>
                {item.organization && (
                  <div className={styles.Row}>
                    <div className={styles.ColText}>Наименование</div>
                    <div className={styles.ColValue}>{parseErknmValue(item.organization.value)?.fullName}</div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </KnmSubtitle>
      {passportData?.pmErknm.events != null && (
        <KnmSubtitle title="Перечень действий, осуществляемых в рамках ПМ">
          {passportData?.pmErknm.events.map((item, index) => (
            <div key={index} className={styles.PmEventContainer}>
              <span className={styles.SectionTitle}>Действие №{index + 1}</span>
              {item.event && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Действие</div>
                  <div className={styles.ColValue}>{parseErknmValue(item.event.value)?.title}</div>
                </div>
              )}
              {item.interactionCategory && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Категория взаимодействия</div>
                  <div className={styles.ColValue}>{item.interactionCategory.name}</div>
                </div>
              )}
              {item.startDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата начала действия</div>
                  <div className={styles.ColValue}>
                    {formatDate({
                      date: item.startDate,
                      dateFormat: DATE_FORMAT_LONG_FNS,
                    })}
                  </div>
                </div>
              )}
              {item.stopDate && (
                <div className={styles.Row}>
                  <div className={styles.ColText}>Дата окончания действия</div>
                  <div className={styles.ColValue}>
                    {formatDate({
                      date: item.stopDate,
                      dateFormat: DATE_FORMAT_LONG_FNS,
                    })}
                  </div>
                </div>
              )}
            </div>
          ))}
        </KnmSubtitle>
      )}
    </div>
  );
});
